import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styles, { colors } from '../../style/rulesEngine';
import {
  FIELD_OPTIONS,
  FIELD_TYPE_MAP,
  NO_VALUE_OPERATORS,
  STATUS_OPTIONS,
  PARTIALLY_SCANNED_SENTINEL,
  COUNTRY_OPTIONS,
  STATE_OPTIONS_US,
  STATE_OPTIONS_CA,
  getOperatorsForField,
  getSkuOperatorOptions,
  SKU_PICKER_OPERATORS,
  SKU_MULTI_TO_SINGLE_OPERATOR,
} from './cascadeConfig';
import RuleEngineDropdown from './RuleEngineDropdown';
import LocationTypeahead from './LocationTypeahead';
import SKUPickerModal from './SKUPickerModal';
import { fetchTenantStores } from '../../actions/storeActions';

function getDisplayedCondition(condition) {
  if (condition.field === 'order_is_partially_scanned') {
    return {
      field:    'order_status',
      operator: condition.operator === 'not_equals' ? 'is_not' : 'is',
      value:    PARTIALLY_SCANNED_SENTINEL,
    };
  }
  return condition;
}

const ConditionRow = ({ condition, index, onChange, onRemove, isOnly, errors }) => {
  const { width } = useWindowDimensions();
  const isNarrow = width < 600;
  const displayed = getDisplayedCondition(condition);
  const fieldType = FIELD_TYPE_MAP[displayed.field] || null;
  const isSkuField = displayed.field === 'order_item_skus';
  const skuValues = isSkuField
    ? (Array.isArray(condition.value) ? condition.value : (condition.value ? [condition.value] : []))
    : [];
  const operatorOptions = isSkuField
    ? getSkuOperatorOptions(skuValues.length)
    : (displayed.field ? getOperatorsForField(displayed.field, fieldType) : []);
  const showValue = !!displayed.operator && !NO_VALUE_OPERATORS.includes(displayed.operator);
  const isBetween = displayed.operator === 'between';
  const isStatusField = displayed.field === 'order_status';
  const isCountryField = displayed.field === 'order_shipping_country';
  const isStateField = displayed.field === 'order_shipping_state';
  const isStoreTypeField = displayed.field === 'order_store_type';
  const isStoreNameField = displayed.field === 'order_store_name';
  const isInList = displayed.operator === 'is_in_list';
  const isSkuPicker = isSkuField && SKU_PICKER_OPERATORS.includes(displayed.operator);
  const [skuPickerOpen, setSkuPickerOpen] = useState(false);
  const skuDisplayLabel = skuValues.length === 0
    ? ''
    : skuValues.length === 1
      ? skuValues[0]
      : `${skuValues[0]} +${skuValues.length - 1} more`;
  const dispatch = useDispatch();
  const storesState = useSelector((state) => state.rules || {});
  const tenantStores = storesState.stores || [];
  const storesLoading = !!storesState.storesLoading;
  const storesError   = storesState.storesError || null;
  const storesNotLoaded = !storesLoading && tenantStores.length === 0 && !storesError;
  const retryStoresFetch = () => dispatch(fetchTenantStores());

  useEffect(() => {
    if ((isStoreTypeField || isStoreNameField) && storesNotLoaded) {
      dispatch(fetchTenantStores());
    }
  }, [isStoreTypeField, isStoreNameField]);

  const storeTypeOptions = Array.from(
    new Set(tenantStores.map((s) => s?.store_type).filter(Boolean))
  ).map((t) => ({ value: t, label: t }));

  const storeNameOptions = tenantStores
    .filter((s) => s?.store_type !== 'system')
    .map((s) => s?.name)
    .filter(Boolean)
    .map((n) => ({ value: n, label: n }));

  const showStoreTypeDropdown = isStoreTypeField;
  const showStoreNameDropdown = isStoreNameField;

  const placeholderForStore = (emptyLabel, readyLabel, optionsCount) => {
    if (storesError)   return 'Could not load stores';
    if (storesLoading) return 'Loading stores…';
    return optionsCount === 0 ? emptyLabel : readyLabel;
  };
  const storeTypePlaceholder = placeholderForStore('No stores found', 'Select store type', storeTypeOptions.length);
  const storeNamePlaceholder = placeholderForStore('No stores found', 'Select store name', storeNameOptions.length);

  const STATE_OPTIONS_COMBINED = [
    ...STATE_OPTIONS_US.map((o) => ({ value: o.value, label: `US — ${o.label}` })),
    ...STATE_OPTIONS_CA.map((o) => ({ value: o.value, label: `CA — ${o.label}` })),
  ];

  const showCountryTypeahead = isCountryField;
  const showStateTypeahead   = isStateField;

  const handleFieldChange = (newField) => {
    onChange(index, { field: newField, operator: '', value: '', value2: '' });
  };

  const handleOperatorChange = (newOperator) => {
    if (condition.field === 'order_is_partially_scanned') {
      if (newOperator === 'is' || newOperator === 'is_not') {
        onChange(index, {
          ...condition,
          operator: newOperator === 'is_not' ? 'not_equals' : 'equals',
          value: true,
        });
      } else {
        onChange(index, { field: 'order_status', operator: newOperator, value: '', value2: '' });
      }
      return;
    }
    if (condition.field === 'order_item_skus') {
      onChange(index, { ...condition, operator: newOperator, value2: '' });
      return;
    }
    onChange(index, { ...condition, operator: newOperator, value: '', value2: '' });
  };

  const filterInt = (val) => (fieldType === 'number' ? val.replace(/[^0-9]/g, '') : val);

  const handleValueChange = (val) => {
    if (isStatusField && val === PARTIALLY_SCANNED_SENTINEL) {
      const isNegated = ['is_not', 'not_equals'].includes(condition.operator);
      onChange(index, {
        ...condition,
        field:    'order_is_partially_scanned',
        operator: isNegated ? 'not_equals' : 'equals',
        value:    true,
      });
      return;
    }

    if (condition.field === 'order_is_partially_scanned' && isStatusField) {
      onChange(index, {
        field:    'order_status',
        operator: condition.operator === 'not_equals' ? 'is_not' : 'is',
        value:    val,
      });
      return;
    }
    onChange(index, { ...condition, value: filterInt(val) });
  };

  const handleValue2Change = (val) => {
    onChange(index, { ...condition, value2: filterInt(val) });
  };

  const fieldError = errors?.[`condition_${index}_field`];
  const operatorError = errors?.[`condition_${index}_operator`];
  const valueError = errors?.[`condition_${index}_value`];

  const rowStyle = [styles.conditionRow, isNarrow && { flexDirection: 'column', flexWrap: 'nowrap', gap: 8, alignItems: 'stretch' }];
  const fieldStyle = [styles.rowField, isNarrow && { flexGrow: 0, flexShrink: 0, flexBasis: 'auto', width: '100%' }];

  return (
    <View style={rowStyle}>
      {/* Subject */}
      <View style={fieldStyle}>
        <Text style={styles.fieldLabel}>Subject</Text>
        <RuleEngineDropdown
          options={FIELD_OPTIONS.map((f) => ({ value: f.value, label: f.label }))}
          selectedValue={displayed.field}
          onSelect={handleFieldChange}
          placeholder="Select field"
          testID={`condition_${index}_field`}
          error={!!fieldError}
        />
        {fieldError && <Text style={styles.errorText}>{fieldError}</Text>}
      </View>

      {/* Operator */}
      {displayed.field && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Operator</Text>
          <RuleEngineDropdown
            options={operatorOptions}
            selectedValue={displayed.operator}
            onSelect={handleOperatorChange}
            placeholder="Select condition"
            testID={`condition_${index}_operator`}
            error={!!operatorError}
          />
          {operatorError && <Text style={styles.errorText}>{operatorError}</Text>}
        </View>
      )}

      {showValue && isSkuPicker && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <TouchableOpacity
            onPress={() => setSkuPickerOpen(true)}
            style={[styles.nameInput, valueError && styles.errorBorder, { justifyContent: 'center' }]}
            testID={`condition_${index}_sku_picker_button`}
          >
            <Text style={{ color: skuDisplayLabel ? colors.text : colors.textMuted }}>
              {skuDisplayLabel || 'Select products…'}
            </Text>
          </TouchableOpacity>
          <SKUPickerModal
            visible={skuPickerOpen}
            onClose={() => setSkuPickerOpen(false)}
            selectedSkus={skuValues}
            onSKUsSelected={(skus) => {
              const revertedOperator = skus.length < 2
                ? (SKU_MULTI_TO_SINGLE_OPERATOR[condition.operator] || condition.operator)
                : condition.operator;
              onChange(index, { ...condition, operator: revertedOperator, value: skus });
            }}
          />
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {/* Selection — plain value input */}
      {showValue && !isBetween && !isInList && !isStatusField && !showCountryTypeahead && !showStateTypeahead && !showStoreTypeDropdown && !showStoreNameDropdown && !isSkuPicker && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <TextInput
            style={[styles.nameInput, valueError && styles.errorBorder]}
            value={String(displayed.value || '')}
            onChangeText={handleValueChange}
            placeholder="Enter value"
            placeholderTextColor={colors.textMuted}
            keyboardType={(fieldType === 'number' || fieldType === 'numeric_list') ? 'numeric' : 'default'}
          />
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {showValue && showCountryTypeahead && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <LocationTypeahead
            options={COUNTRY_OPTIONS}
            selectedValue={displayed.value}
            onChange={handleValueChange}
            placeholder="Type or pick a country"
            testID={`condition_${index}_country`}
            error={!!valueError}
          />
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {showValue && showStateTypeahead && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <LocationTypeahead
            options={STATE_OPTIONS_COMBINED}
            selectedValue={displayed.value}
            onChange={handleValueChange}
            placeholder="Type or pick a state / province"
            testID={`condition_${index}_state`}
            error={!!valueError}
          />
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {showValue && showStoreTypeDropdown && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <RuleEngineDropdown
            options={storesError || storesLoading ? [] : storeTypeOptions}
            selectedValue={displayed.value}
            onSelect={handleValueChange}
            placeholder={storeTypePlaceholder}
            emptyMessage={storesError ? 'Could not load stores' : (storesLoading ? 'Loading stores…' : undefined)}
            testID={`condition_${index}_store_type`}
            error={!!valueError || !!storesError}
          />
          {storesError && (
            <TouchableOpacity
              onPress={retryStoresFetch}
              testID={`condition_${index}_store_type_retry`}
            >
              <Text style={[styles.errorText, { color: colors.accent }]}>
                Tap to retry
              </Text>
            </TouchableOpacity>
          )}
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {showValue && showStoreNameDropdown && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <RuleEngineDropdown
            options={storesError || storesLoading ? [] : storeNameOptions}
            selectedValue={displayed.value}
            onSelect={handleValueChange}
            placeholder={storeNamePlaceholder}
            emptyMessage={
              storesError ? 'Could not load stores'
                : storesLoading ? 'Loading stores…'
                : 'Create a store first'
            }
            testID={`condition_${index}_store_name`}
            error={!!valueError || !!storesError}
          />
          {storesError && (
            <TouchableOpacity
              onPress={retryStoresFetch}
              testID={`condition_${index}_store_name_retry`}
            >
              <Text style={[styles.errorText, { color: colors.accent }]}>
                Tap to retry
              </Text>
            </TouchableOpacity>
          )}
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {/* Selection — status dropdown */}
      {showValue && isStatusField && !isInList && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <RuleEngineDropdown
            options={STATUS_OPTIONS}
            selectedValue={displayed.value}
            onSelect={handleValueChange}
            placeholder="Select status"
            testID={`condition_${index}_status`}
            error={!!valueError}
          />
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {/* Selection — multi-value list input */}
      {showValue && isStatusField && isInList && (
        <View style={fieldStyle}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <RuleEngineDropdown
            multiSelect
            options={STATUS_OPTIONS.filter((o) => o.value !== PARTIALLY_SCANNED_SENTINEL)}
            selectedValues={Array.isArray(displayed.value) ? displayed.value : []}
            onSelect={(newArray) => handleValueChange(newArray)}
            placeholder="Select statuses…"
            testID={`condition_${index}_status_list`}
            error={!!valueError}
          />
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {/* Selection — between (two compact inputs) */}
      {isBetween && (
        <View style={isNarrow ? { width: '100%' } : { flexShrink: 1 }}>
          <Text style={styles.fieldLabel}>Selection</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <TextInput
              style={[styles.betweenInput, isNarrow && { flexShrink: 1, flexGrow: 1, minWidth: 60 }, valueError && styles.errorBorder]}
              value={String(displayed.value || '')}
              onChangeText={handleValueChange}
              placeholder="From"
              placeholderTextColor={colors.textMuted}
              keyboardType="numeric"
            />
            <Text style={styles.betweenAndText}>and</Text>
            <TextInput
              style={[styles.betweenInput, isNarrow && { flexShrink: 1, flexGrow: 1, minWidth: 60 }, valueError && styles.errorBorder]}
              value={String(condition.value2 || '')}
              onChangeText={handleValue2Change}
              placeholder="To"
              placeholderTextColor={colors.textMuted}
              keyboardType="numeric"
            />
          </View>
          {valueError && <Text style={styles.errorText}>{valueError}</Text>}
        </View>
      )}

      {/* Remove button */}
      {!isOnly && (
        <TouchableOpacity onPress={() => onRemove(index)} style={styles.removeButton}>
          <i className="icon-trash" style={{ color: '#FFFFFF', fontSize: 20 }} />
        </TouchableOpacity>
      )}
    </View>
  );
};

ConditionRow.propTypes = {
  condition: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isOnly: PropTypes.bool.isRequired,
  errors: PropTypes.object,
};

export default ConditionRow;
